import React, { FC, useEffect } from 'react';
import { max } from 'lodash';
import { Placeholder } from 'rsuite';
import 'wb-reddit/src/app/routes/superGrid/cellRenderers/loading.scss';
import { RedditColors } from 'wb-reddit/app/routes/superGrid/utils';
import classNames from 'classnames';
import {
	Button, ButtonAppearance, ButtonColor, WarningExclamation,
} from '@aleph/shared-components';
import { useNavigate } from 'react-router-dom';
import { ILoadingCellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

const LoadingCellRenderer: FC<ILoadingCellRendererParams> = (params) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	useEffect(() => {
		if (params.node?.failedLoad && params.node?.rowHeight === 144) {
			params.node?.setRowHeight(350);
			params.api.onRowHeightChanged();
		}
	}, [params.node?.failedLoad, params.node?.rowHeight]);

	if (params.node.failedLoad) {
		return (
			<div className="flex w-full items-center justify-center"
				style={{ backgroundImage: 'linear-gradient(180deg, #FFFFFF 59.37%, rgba(255, 255, 255, 0) 100%)' }}
			>
				<div className="flex flex-col items-center justify-center gap-[16px]">
					<div className='flex w-[40px] h-[40px]'>
						<WarningExclamation fill="hsl(var(--gray-500))"/>
					</div>
					<div className='flex flex-col items-center justify-center gap-[8px]'>
						<div className="!text-[16px] !leading-[20px] !text-gray-500 font-medium">
							{t('sc: data_load_fail.title', 'Loading the data failed due to a problem on our end.')}
						</div>
						<div className="!text-[14px] !leading-[17px] !text-gray-500 font-medium">
							{t('sc: data_load_fail.body', 'Apologies for the inconvenience. Please try again in a few moments.')}
						</div>
					</div>
					<div className="flex gap-[16px]">
						<Button
							title="Contact support"
							onClick={() => {
								window.open('https://knowledge.wise.blue', '_blank');
								params.api.refreshCells();
							}}
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.GRAY}
						/>

						<Button
							title="Reset view"
							onClick={() => navigate('/superGrid')}
						/>
					</div>
				</div>
			</div>
		);
	}

	const individualPinnedWidths = [35, 47, 35, undefined, 35, 35];

	const pinnedWidth = max(params.columnApi.getDisplayedLeftColumns().map(col => col.getRight()));
	const anchorColor = RedditColors[params?.context?.gridTopLevel] ?? 'gray-300';

	const LoadingRow = (
		<div style={{ borderBottom: '1px solid hsl(var(--gray-300))', display: 'flex', height: 36 }}>
			<div
				style={{ minWidth: pinnedWidth }}
				className="h-full flex"
			>
				{
					individualPinnedWidths.map((width, idx) => (
						<div
							key={idx}
							className={
								classNames(
									'border-r border-r-gray-300 h-full flex items-center',
								)
							}
							style={{
								flex:            width ? 'unset' : '1',
								borderLeftWidth: idx === 2 ? 1 : undefined,
								borderLeftColor: idx === 2 ? anchorColor : undefined,
							}}
						>
							<div
								className="px-[8px]"
								style={{ width: width ?? '100%' }}
							>
								<Placeholder.Graph
									height={idx === 1 ? 16 : 12}
									active
								/>
							</div>
						</div>
					))
				}
			</div>
			<div
				className="flex items-center h-full"
			>
				{
					(params.columnApi.getDisplayedCenterColumns() ?? []).map((col, idx) => (
						<div
							key={idx}
							style={{ width: (col.getActualWidth() ?? 200) }}
							className="h-full flex items-center border-r border-r-gray-300 px-[8px]"
						>
							<Placeholder.Graph
								height={12}
								active
							/>
						</div>
					))
				}
			</div>
		</div>
	);

	return (
		<div className="h-full w-full loading-row">
			{LoadingRow}
			{LoadingRow}
			{LoadingRow}
			{LoadingRow}
		</div>
	);
};

export default LoadingCellRenderer;
